/**
 * The drive's Shared page — a Projects/MiniApps-style surface (NOT a folder
 * listing) with two tabs:
 *
 *   Shared with me (default) — every incoming `.shared` mount in the tenant,
 *     wherever it lives, as project-style cards grouped under a source-org
 *     heading. Revoked mounts grey out with a "No longer shared" footer badge.
 *   Shared by me — active drive-to-drive grants, one card per
 *     (folder, permission level), recipient facepile in the footer.
 *
 * Chrome matches ProjectsView: search, sort, facet filter, and the
 * org-admin "Show as admin" toggle (server-verified via ?asAdmin=1).
 * Data: GET /api/files/shared-overview.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, UsersRound, ArrowDownLeft, ArrowUpRight, ShieldCheck, Search, ArrowUpDown, Filter, X, Trash2, MoreVertical, FolderOpen, FolderTree, ExternalLink, Download, Info, Pencil, Share2, CircleCheck, EyeOff, Lock } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '@papan/i18n'
import { getActiveTenantId, getActiveOrgEmail } from '@papan/api-client'
import { PointerDownloadDialog, routePointerDownload, type PointerRowLike } from '../PointerDownloadDialog'
import { avatarBg, getInitials, FileIcon, useToast, useConfirm, useOrgMemberNames, useTheme, getThemeClass, FileContextMenu, GlassToggle, Tooltip, useAnchoredPosition, useMyCapabilities, type FileMenuItem } from '@papan/core-ui/ui'
import { sessionSignal } from '@papan/core-ui/ui/sessionEnded'
import { filesApi, type BinItem } from '@papan/files/client'
import { setActiveDragFolderIds, clearActiveDrag, createDragImage, offerDragToApps } from '@papan/files/client/dnd'
import { startPapanDrag } from '@papan/core-ui/ui/papanDrag'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { ShareDialog } from '@papan/org-connections/ui'
import { WhoHasAccessDialog } from '../WhoHasAccessDialog'
import { ShareInternallyDialog } from '../ShareInternallyDialog'
import { FileInfoDialog } from '../FileInfoDialog'
import { formatRelativeDate } from '../formatters'
import { restoreRefusalMessage } from '../restore-refusal'
import { ProjectAccessLine } from '../AccessSummaryBits'
import type { FileItem } from '../useFiles'
import { projectHue, useSharedOverview, type IncomingShare } from './data'


type SharedSort = 'modified' | 'shared' | 'name'
type SharedFacet = { type: 'org' | 'recipient' | 'perm'; value: string; label: string }

/** Small round org-initials badge (avatarBg is a CLASS, like ProjectCard uses). */
/** The three incoming lenses (see the `view` state). */
const VIEWS = [
  { key: 'active' as const, icon: CircleCheck, labelKey: 'driveHome.sharedPage.viewActive', fallback: 'Active shared' },
  { key: 'missing' as const, icon: Trash2, labelKey: 'driveHome.sharedPage.viewMissing', fallback: 'Missing shared' },
  { key: 'noaccess' as const, icon: EyeOff, labelKey: 'driveHome.sharedPage.viewNoAccess', fallback: 'No access shared' },
]

/** Parent-location segments of a full VFS path — root ('papan') and the
 *  folder's own name stripped. `/papan/Library/dev` → `['Library']`. */
function folderLocation(path: string | null | undefined): string[] {
  if (!path) return []
  const segs = path.replace(/^\/+/, '').split('/')
  segs.shift() // drop the 'papan' root
  segs.pop()   // drop the folder's own name (already the Folder column)
  return segs
}

const orgBadge = (name: string | null, extra = '') => (
  <span
    title={name ?? undefined}
    className={`rounded-full inline-flex items-center justify-center font-semibold text-white shrink-0 ${avatarBg(name ?? '?')} ${extra}`}
  >
    {getInitials(name ?? '?')}
  </span>
)

export function SharedView({ urlPrefix, isOrg }: {
  urlPrefix: string
  isOrg?: boolean
  orgEmail?: string
}) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()
  // Real grantee names for the access lines below — one shared query.
  const { data: memberNames } = useOrgMemberNames()
  const tenantId = getActiveTenantId() ?? ''

  const [tab, setTab] = useState<'incoming' | 'outgoing'>('incoming')
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<SharedSort>('modified')
  const [facet, setFacet] = useState<SharedFacet | null>(null)
  const [sortOpen, setSortOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [showAsAdmin, setShowAsAdmin] = useState(false)
  /**
   * Incoming lens:
   *   active   — live mounts (the default view)
   *   missing  — `.shared` rows deleted from this drive, restorable from Bin
   *   noaccess — mounts whose grant was revoked. Deliberately NOT grouped or
   *              labelled by source org: the grant is gone, so claiming who
   *              shared it would state a relationship that no longer holds.
   */
  const [view, setView] = useState<'active' | 'missing' | 'noaccess'>('active')
  const [accessTarget, setAccessTarget] = useState<{ folderId: string; folderName: string; canManage: boolean; isFolder: boolean; initialViewMode?: 'people' | 'outsider' | 'share-links' | 'miniapp' } | null>(null)
  const [detailsTarget, setDetailsTarget] = useState<FileItem | null>(null)
  const [shareLinkTarget, setShareLinkTarget] = useState<IncomingShare | null>(null)
  const [shareOrgTarget, setShareOrgTarget] = useState<IncomingShare | null>(null)
  const [shareInternalTarget, setShareInternalTarget] = useState<IncomingShare | null>(null)
  const [renameTarget, setRenameTarget] = useState<IncomingShare | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // ⋮ menu — the shared glass FileContextMenu, portaled to <body>.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  /**
   * An incoming share is a `.shared` pointer, so a Download here has the same
   * two possible outcomes it has in the drive -- the source content, or the
   * pointer file that recreates the share elsewhere. Both call sites called
   * `downloadItem` directly, which always takes CONTENTS, so the choice was
   * simply absent from this page.
   *
   * `routePointerDownload` is the drive's rule; `broken` is this page's name
   * for `shareBroken`.
   *
   * The `'metadata'` branch is UNREACHABLE here and that is deliberate, not an
   * oversight: both entry points sit inside `!s.broken`, because this page
   * decided a revoked mount offers only Remove. So the branch is a
   * pass-through for the shared rule rather than a route this surface takes.
   * If that product decision is ever revisited -- the JSON is the only thing
   * that repairs a broken share -- this already does the right thing.
   */
  const [pointerDownload, setPointerDownload] = useState<PointerRowLike | null>(null)
  const saveShareContents = (row: PointerRowLike) =>
    void filesApi.downloadItem({ id: row.id, name: row.name, isFolder: row.isFolder, isPointer: true })
      .catch((e) => toast.error((e as Error)?.message || t('driveHome.sharedPage.downloadFailed', { defaultValue: 'Download failed' })))
  const saveShareFile = (row: PointerRowLike) =>
    void filesApi.downloadMetadata(row.id)
      .catch((e) => toast.error((e as Error)?.message || t('driveHome.sharedPage.downloadFailed', { defaultValue: 'Download failed' })))
  const downloadShare = (s: { id: string; name: string; isFolder?: boolean; broken?: boolean }) => {
    const row: PointerRowLike = {
      id: s.id, name: s.name, isFolder: s.isFolder !== false,
      fileType: 'shared', shareBroken: s.broken,
    }
    const route = routePointerDownload(row)
    if (route === 'metadata') return saveShareFile(row)
    if (route === 'choose') { setPointerDownload(row); return }
    return saveShareContents(row)
  }
  // `anchorEl` rather than coordinates: the trigger is a different button per
  // card, so there is no single ref to hold. The element is what
  // `useAnchoredPosition` follows and observes for exit.
  const [menu, setMenu] = useState<{ share: IncomingShare; anchorEl: HTMLElement } | null>(null)
  const menuAnchorRef = useRef<HTMLElement | null>(null)
  menuAnchorRef.current = menu?.anchorEl ?? null
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menu) return
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return
      // Leave the ⋮ trigger alone: this runs on mousedown, so closing here
      // would clear `menu` before the button's own click handler reads it —
      // its toggle-shut branch could never fire and the menu just reopened.
      // The button owns that case; every other target closes.
      if ((e.target as HTMLElement | null)?.closest?.('[data-testid="shared-card-menu"]')) return
      setMenu(null)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menu])

  // Shared anchored-panel convention: follow the card, play out when it leaves.
  const { pos: menuPos, exiting: menuExiting, origin: menuOrigin } = useAnchoredPosition({
    open: !!menu,
    anchorRef: menuAnchorRef,
    floatingRef: menuRef,
    onDismiss: useCallback(() => setMenu(null), []),
    // One ref, many ⋮ buttons: clicking a DIFFERENT card repoints it without
    // closing, and React cannot see that from the ref object alone.
    anchorKey: menu?.share.id ?? null,
    exitTransitionMs: 160,
  })

  // #1102 item 3 — "Show as admin" is offered to who holds tenant-wide `manage_folder_permission:write` (never the role;
  // defaults Owner and Admin); the server re-checks it and reveals a row's location only where the caller manages it
  const { can: canCap } = useMyCapabilities(isOrg ? tenantId : undefined, !isOrg, getActiveOrgEmail() ?? undefined)
  const showAdminToggle = !!isOrg && canCap('manage_folder_permission', 'write')
  const overview = useSharedOverview(showAdminToggle && showAsAdmin)

  // "Show inactive" also surfaces DELETED `.shared` rows straight from the
  // Bin, restorable in place — fetched only while the toggle is on.
  const bin = useQuery({
    queryKey: ['shared-bin', getActiveTenantId(), getActiveOrgEmail()],
    // #894: the Bin is paged, so ask it for exactly the deleted `.shared` items
    // (every page of them) rather than reading a first page of everything.
    queryFn: async () => {
      const myBin: BinItem[] = []
      let cursor: string | null = null
      for (let pages = 0; pages < 50; pages++) {
        const page = await filesApi.listBinSection('mine', cursor, { kind: 'shared' })
        myBin.push(...page.items)
        cursor = page.nextCursor
        if (!cursor) break
      }
      return { myBin }
    },
    staleTime: 30_000,
    refetchOnMount: 'always', // same unmount-while-in-drive reason as above
    enabled: tab === 'incoming' && view === 'missing',
  })
  // One entry per NAME, newest deletion first — repeated share/delete cycles
  // leave several bin rows named `ddd.shared`, and only the latest is a
  // meaningful restore target (a .shared is id-bound to source+recipient;
  // older copies are stale). `olderCount` hints at the rest, which stay
  // reachable in the Bin page until retention expires them.
  const deletedShares = useMemo(() => {
    // The search box applies here too — it sits above every lens, so leaving
    // this list unfiltered made the control look broken on Missing.
    const q = filter.trim().toLowerCase()
    const rows = (bin.data?.myBin ?? [])
      .filter(i => /\.shared$/i.test(i.originalName))
      .filter(i => !q || i.originalName.toLowerCase().includes(q))
      .sort((a, b) => b.deletedAt.localeCompare(a.deletedAt))
    const byName = new Map<string, BinItem & { olderCount: number }>()
    for (const i of rows) {
      const key = i.originalName.toLowerCase()
      const existing = byName.get(key)
      if (existing) existing.olderCount += 1
      else byName.set(key, { ...i, olderCount: 0 })
    }
    return [...byName.values()]
  }, [bin.data, filter])

  const PERM_LABEL: Record<string, string> = {
    readcopy: t('driveHome.sharedPage.perm.readcopy', { defaultValue: 'Read-only' }),
    readwrite: t('driveHome.sharedPage.perm.readwrite', { defaultValue: 'Read & Write' }),
    admin: t('driveHome.sharedPage.perm.admin', { defaultValue: 'Manage & Edit' }),
    hidden: t('driveHome.sharedPage.perm.hidden', { defaultValue: 'Hidden' }),
  }
  const SORT_LABEL: Record<SharedSort, string> = {
    modified: t('driveHome.projectsPage.sortModified', { defaultValue: 'Last modified' }),
    shared: t('driveHome.sharedPage.sortShared', { defaultValue: 'Last shared' }),
    name: t('driveHome.projectsPage.sortName', { defaultValue: 'Name' }),
  }

  const incoming = overview.data?.incoming ?? []
  const outgoing = overview.data?.outgoing ?? []

  // ── Incoming: search + facet + sort, grouped by source org ──────────
  // Revoked mounts are "inactive" — hidden by default, shown by the toggle.
  const visibleIncoming = useMemo(() => {
    const q = filter.trim().toLowerCase()
    let items = incoming
    // `missing` renders from the Bin instead, so it has no live rows.
    items = view === 'noaccess' ? items.filter(s => s.broken)
      : view === 'missing' ? []
        : items.filter(s => !s.broken)
    if (q) items = items.filter(s => s.name.toLowerCase().includes(q) || (s.sourceTenantName ?? '').toLowerCase().includes(q))
    if (facet?.type === 'org') items = items.filter(s => s.sourceTenantId === facet.value)
    const byName = (a: IncomingShare, b: IncomingShare) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    const byDate = (field: 'updatedAt' | 'sharedAt') => (a: IncomingShare, b: IncomingShare) =>
      (b[field] ?? '').localeCompare(a[field] ?? '') || byName(a, b)
    const cmp = sort === 'name' ? byName : sort === 'shared' ? byDate('sharedAt') : byDate('updatedAt')
    return [...items].sort(cmp)
  }, [incoming, filter, facet, sort, view])

  const incomingByOrg = useMemo(() => {
    const groups = new Map<string, { tenantId: string | null; name: string | null; items: IncomingShare[] }>()
    for (const s of visibleIncoming) {
      const key = s.sourceTenantId ?? '∅'
      let grp = groups.get(key)
      if (!grp) { grp = { tenantId: s.sourceTenantId, name: s.sourceTenantName, items: [] }; groups.set(key, grp) }
      grp.items.push(s)
    }
    return [...groups.values()].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }, [visibleIncoming])

  // ── Outgoing: one card per (folder, level) + search/facet/sort ──────
  const PERM_ORDER: Record<string, number> = { admin: 0, readwrite: 1, readcopy: 2 }
  const outgoingCards = useMemo(() => {
    const q = filter.trim().toLowerCase()
    let items = outgoing
    if (q) items = items.filter(g => g.folderName.toLowerCase().includes(q) || (g.recipientTenantName ?? '').toLowerCase().includes(q))
    if (facet?.type === 'recipient') items = items.filter(g => g.recipientTenantId === facet.value)
    if (facet?.type === 'perm') items = items.filter(g => g.permission === facet.value)
    const groups = new Map<string, { folderId: string; folderName: string; permission: string; canManage: boolean; isFolder: boolean; path: string | null; parentId: string | null; latest: string; recipients: Array<{ id: string; name: string | null }> }>()
    for (const g of items) {
      const key = `${g.folderId}:${g.permission}`
      let grp = groups.get(key)
      if (!grp) {
        grp = { folderId: g.folderId, folderName: g.folderName, permission: g.permission, canManage: g.canManage, isFolder: g.isFolder !== false, path: g.path ?? null, parentId: g.parentId ?? null, latest: g.createdAt ?? '', recipients: [] }
        groups.set(key, grp)
      }
      if ((g.createdAt ?? '') > grp.latest) grp.latest = g.createdAt ?? ''
      grp.recipients.push({ id: g.recipientTenantId, name: g.recipientTenantName })
    }
    const byName = (a: { folderName: string; permission: string }, b: { folderName: string; permission: string }) =>
      a.folderName.localeCompare(b.folderName) || ((PERM_ORDER[a.permission] ?? 9) - (PERM_ORDER[b.permission] ?? 9))
    return [...groups.values()].sort(sort === 'name' ? byName : (a, b) => (b.latest.localeCompare(a.latest) || byName(a, b)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoing, filter, facet, sort])

  // Facet catalogs per tab (from the unfiltered data).
  const facetGroups = useMemo((): Array<{ label: string; options: SharedFacet[] }> => {
    if (tab === 'incoming') {
      const orgs = new Map<string, SharedFacet>()
      for (const s of incoming) {
        if (s.sourceTenantId && !orgs.has(s.sourceTenantId)) {
          orgs.set(s.sourceTenantId, { type: 'org', value: s.sourceTenantId, label: s.sourceTenantName ?? '?' })
        }
      }
      return [{ label: t('driveHome.sharedPage.facetOrg', { defaultValue: 'Shared by' }), options: [...orgs.values()].sort((a, b) => a.label.localeCompare(b.label)) }]
    }
    const recipients = new Map<string, SharedFacet>()
    const perms = new Map<string, SharedFacet>()
    for (const g of outgoing) {
      if (!recipients.has(g.recipientTenantId)) recipients.set(g.recipientTenantId, { type: 'recipient', value: g.recipientTenantId, label: g.recipientTenantName ?? '?' })
      if (!perms.has(g.permission)) perms.set(g.permission, { type: 'perm', value: g.permission, label: PERM_LABEL[g.permission] ?? g.permission })
    }
    return [
      { label: t('driveHome.sharedPage.facetRecipient', { defaultValue: 'Shared to' }), options: [...recipients.values()].sort((a, b) => a.label.localeCompare(b.label)) },
      { label: t('driveHome.sharedPage.facetPermission', { defaultValue: 'Access level' }), options: [...perms.values()].sort((a, b) => (PERM_ORDER[a.value] ?? 9) - (PERM_ORDER[b.value] ?? 9)) },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, incoming, outgoing, t])

  const switchTab = (next: 'incoming' | 'outgoing') => { setTab(next); setFacet(null); setSortOpen(false); setFilterOpen(false) }
  const stop = (e: React.MouseEvent) => e.stopPropagation()

  const permBadge = (p: string) => (
    <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 themed-bg-primary themed-text-secondary shrink-0 whitespace-nowrap">
      {PERM_LABEL[p] ?? p}
    </span>
  )

  /* ── Shared with me: consume-oriented tiles. Org identity lives in the
     section heading; the card carries what the share IS (name, kind, size),
     what you can DO with it (access badge), and its timeline (shared /
     modified). The whole tile opens the share. ────────────────────────── */

  // A share presents as its TARGET: folder-target shares browse as a folder,
  // file-target shares open in the single-file viewer (same split as
  // DriveView's handleFileOpen — including the dead-pointer block).
  const openIncoming = (s: IncomingShare) => {
    if (s.broken) {
      toast.info(t('driveHome.sharedPage.revokedHint', {
        defaultValue: 'This shared item is no longer available — access was revoked. You can remove it from your drive.',
      }))
      return
    }
    navigate(s.isFolder !== false ? `${urlPrefix}/folder/${s.id}` : `${urlPrefix}/file/${s.id}`)
  }

  // Rename the MOUNT row in this drive (the source name is untouched). The
  // `.shared` ext is preserved — stripping it converts the row to a plain
  // file, which is a different (destructive-feeling) operation.
  const startRename = (s: IncomingShare) => {
    setRenameValue(s.name.replace(/\.shared$/i, ''))
    setRenameTarget(s)
  }
  const commitRename = async () => {
    const s = renameTarget
    const value = renameValue.trim()
    setRenameTarget(null)
    if (!s || !value) return
    const ext = s.name.slice(s.name.replace(/\.shared$/i, '').length) // keeps original casing
    const newName = value + ext
    if (newName === s.name) return
    try {
      await filesApi.moveFile(s.id, { name: newName })
      toast.success(t('driveView.toast.renamed', { oldName: s.name, newName, defaultValue: `Renamed to “${newName}”` }))
      void overview.refetch()
    } catch (e) {
      toast.error((e as Error)?.message || t('driveView.toast.renameFailed', { defaultValue: 'Rename failed' }))
    }
  }

  // Restore an accidentally-deleted `.shared` row straight from the Bin (back
  // to its original location; the server re-checks permission + conflicts).
  const restoreDeleted = async (i: BinItem) => {
    // E7 PR 0b: a "still verifying" restore outlives navigation (the toasts are app-wide); it stops, saying nothing,
    // only when the session ends
    // Inline, not the restore job: a `.shared` pointer row is one small row, never a big tree (#921).
    const signal = sessionSignal()
    try {
      await filesApi.restoreFromBin(i.id, undefined, { signal })
      if (signal.aborted) return  // the session ended while it ran: nothing is told
      toast.success(t('driveHome.sharedPage.restored', { name: i.originalName, defaultValue: `Restored “${i.originalName}”` }))
      void bin.refetch()
      void overview.refetch()
    } catch (e) {
      if (signal.aborted) return
      // e.g. the original parent is gone — the Bin page offers a location picker.
      // A refusal the Bin explains (uploads paused, an app safeguard) reads the same here.
      toast.error(restoreRefusalMessage(t, e, i.originalName) ?? ((e as Error)?.message || t('driveHome.sharedPage.restoreFailed', { defaultValue: 'Restore failed' })))
    }
  }

  // Remove a revoked mount from THIS drive (revoke greys it out on purpose —
  // cleanup is the recipient's call). Plain drive delete: the row goes to Bin.
  const removeBroken = async (s: IncomingShare) => {
    const ok = await confirm({
      title: t('driveView.confirm.deleteTitle', { defaultValue: 'Delete' }),
      message: t('driveView.confirm.deleteFile', { name: s.name, defaultValue: `Are you sure you want to delete "${s.name}"?` }),
      type: 'danger',
      confirmText: t('driveHome.sharedPage.remove', { defaultValue: 'Remove' }),
    })
    if (!ok) return
    try {
      // Inline, not the delete job: a revoked mount row is one pointer row, never a big tree (#921).
      await filesApi.deleteFile(s.id)
      toast.success(t('driveView.toast.deletedOne', { name: s.name, defaultValue: `Deleted "${s.name}"` }))
      void overview.refetch()
      void bin.refetch() // the row just moved into the "Missing shared" lens
    } catch (e) {
      toast.error((e as Error)?.message || t('driveView.toast.deleteFailed', { defaultValue: 'Delete failed' }))
    }
  }

  // Cards are draggable with the SAME drag drive rows start (`startPapanDrag`), so the
  // existing drop targets (nav folders, taskbar drive icon, workspace tree)
  // accept them — the `_shared` folder view used to be the only way to move a
  // mount out of its landing folder.
  const dragProps = (s: IncomingShare) => ({
    draggable: !s.broken,
    onDragStart: (e: React.DragEvent) => {
      const payload = { id: s.id, name: s.name, isFolder: s.isFolder !== false }
      startPapanDrag(e.dataTransfer, { tenantId, files: [payload] })
      // …and to an app it is dropped on, as content (#605).
      offerDragToApps(e.dataTransfer, [s.id])
      if (payload.isFolder) setActiveDragFolderIds([s.id])
      const ghost = createDragImage([{ name: s.name }])
      document.body.appendChild(ghost)
      e.dataTransfer.setDragImage(ghost, 0, 0)
      requestAnimationFrame(() => ghost.remove())
    },
    onDragEnd: () => clearActiveDrag(),
  })

  // Card ⋮ actions. A share is a MOUNT, so the catalog is the mount's own
  // verbs — open/browse, download a copy, details, and remove-from-my-drive.
  // (Sharing it onward, renaming the source, etc. live in the target's own
  // drive view; a revoked mount only offers Remove.)
  const buildShareMenu = (s: IncomingShare): FileMenuItem[] => {
    const items: FileMenuItem[] = []
    const asFile = { id: s.id, name: s.name, isFolder: s.isFolder !== false, updatedAt: s.updatedAt ?? undefined } as FileItem
    if (!s.broken) {
      items.push({
        kind: 'item', icon: s.isFolder !== false ? FolderOpen : ExternalLink,
        label: s.isFolder !== false
          ? t('driveHome.projects.browseFiles', { defaultValue: 'Browse files' })
          : t('driveView.menu.openInNewTab', { defaultValue: 'Open' }),
        testId: 'scm-open',
        onClick: () => { setMenu(null); openIncoming(s) },
      })
      items.push({
        kind: 'item', icon: Pencil, label: t('driveView.menu.rename', { defaultValue: 'Rename' }), testId: 'scm-rename',
        onClick: () => { setMenu(null); startRename(s) },
      })
      // Sharing verbs, gated on the CALLER's own power over the mount
      // (server-computed `canManage` = owner/admin on the local row), NOT on
      // the source-grant badge. An org owner manages the mount in their own
      // drive even when the source granted only readcopy; a plain readcopy
      // member does not, and hiding these there avoids a row of 403 toasts.
      if (s.canManage) {
        items.push({ kind: 'separator' })
        items.push({
          kind: 'item', icon: UsersRound, label: t('driveView.shareInternally.action', { defaultValue: 'Share internally' }), testId: 'scm-share-internal',
          onClick: () => { setMenu(null); setShareInternalTarget(s) },
        })
        items.push({
          kind: 'item', icon: Share2, label: t('driveView.menu.shareLink', { defaultValue: 'Share link' }), testId: 'scm-share-link',
          onClick: () => { setMenu(null); setShareLinkTarget(s) },
        })
        items.push({
          kind: 'item', icon: Share2, label: t('driveView.menu.shareToDrive', { defaultValue: 'Share to another Drive' }), testId: 'scm-share-org',
          onClick: () => { setMenu(null); setShareOrgTarget(s) },
        })
        items.push({
          kind: 'item', icon: Users, label: t('driveView.menu.whoHasAccess', { defaultValue: 'Who has access...' }), testId: 'scm-who',
          // isFolder from the row, not hardcoded true — a file-target mount
          // must NOT get the folder-only Restricted toggle (it 404s on PATCH).
          onClick: () => { setMenu(null); setAccessTarget({ folderId: s.id, folderName: s.name, canManage: true, isFolder: s.isFolder !== false }) },
        })
      }
      items.push({ kind: 'separator' })
      items.push({
        kind: 'item', icon: Download, label: s.isFolder !== false ? t('driveView.menu.downloadAsZip') : t('driveView.menu.download'), testId: 'scm-download',
        onClick: () => {
          setMenu(null)
          downloadShare(s)
        },
      })
      items.push({
        kind: 'item', icon: Info, label: t('driveView.menu.details', { defaultValue: 'Details' }), testId: 'scm-details',
        onClick: () => { setMenu(null); setDetailsTarget(asFile) },
      })
      items.push({ kind: 'separator' })
    }
    items.push({
      kind: 'item', icon: Trash2, danger: true,
      label: t('driveHome.sharedPage.removeFromDrive', { defaultValue: 'Remove from my drive' }),
      testId: 'scm-remove',
      onClick: () => { setMenu(null); void removeBroken(s) },
    })
    return items
  }

  const incomingCard = (s: IncomingShare, orgName: string | null) => (
    <div
      key={s.id}
      role="button"
      tabIndex={0}
      {...dragProps(s)}
      onClick={() => openIncoming(s)}
      onKeyDown={(e) => { if (e.key === 'Enter') openIncoming(s) }}
      className={`ws-project-card group text-left cursor-pointer ${s.broken ? 'opacity-55' : ''}`}
      style={{ '--pc-hue': projectHue(s.name) } as CSSProperties}
      data-testid="shared-incoming-card"
    >
      {/* Media object: badge (glyph + source-org avatar) · content column
          (title · "Shared X ago" · icon buttons) · perm badge + ⋮ —
          the same anatomy as the mini-app cards. */}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 mx-1.5">
          {/* Carved well holding the drive's plain glyph — yellow folder /
              white file. `type` (not `name`) so the .shared ext can't pull
              in the mount overlay. */}
          <span aria-hidden className="ws-project-glyph">
            <FileIcon type={s.isFolder !== false ? 'folder' : 'file'} isFolder={s.isFolder !== false} size={18} />
          </span>
          {/* Source-org badge — omitted when the caller passes no org (the
              No-access lens deliberately doesn't attribute revoked mounts). */}
          {orgName && (
            <span aria-hidden className={`ws-card-owner-ring absolute -bottom-1 -right-1 w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-semibold text-white ${avatarBg(orgName)}`}>
              {getInitials(orgName)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-medium themed-text truncate">{s.name}</span>
          {/* Subtitle follows the sort: 'Last modified' → "Modified X ago",
              otherwise (Last shared / Name) → "Shared X ago". Each falls back
              to the other date when its own is missing. */}
          <span className="block text-[10px] themed-text-muted truncate mt-0.5">
            {s.broken
              ? t('driveHome.sharedPage.noLongerShared', { defaultValue: 'No longer shared' })
              : sort === 'modified'
                ? (s.updatedAt
                  ? t('driveHome.projectsPage.modifiedAgo', { when: formatRelativeDate(s.updatedAt), defaultValue: `Modified ${formatRelativeDate(s.updatedAt)}` })
                  : s.sharedAt
                    ? t('driveHome.sharedPage.sharedAgo', { when: formatRelativeDate(s.sharedAt), defaultValue: `Shared ${formatRelativeDate(s.sharedAt)}` })
                    : '—')
                : (s.sharedAt
                  ? t('driveHome.sharedPage.sharedAgo', { when: formatRelativeDate(s.sharedAt), defaultValue: `Shared ${formatRelativeDate(s.sharedAt)}` })
                  : s.updatedAt
                    ? t('driveHome.projectsPage.modifiedAgo', { when: formatRelativeDate(s.updatedAt), defaultValue: `Modified ${formatRelativeDate(s.updatedAt)}` })
                    : '—')}
          </span>

          {/* Icon buttons — the height-giving row, like the mini-app card.
              A revoked mount can't be opened or downloaded, so it offers only
              Remove instead. */}
          <div className="flex items-center gap-0.5 mt-1.5" onClick={stop}>
            {s.broken ? (
              <Tooltip always content={t('driveHome.sharedPage.remove', { defaultValue: 'Remove' })}>
                <button
                  onClick={(e) => { stop(e); void removeBroken(s) }}
                  className="ws-card-iconbtn hover:text-red-400"
                  aria-label={t('driveHome.sharedPage.remove', { defaultValue: 'Remove' })}
                  data-testid="shared-incoming-remove"
                >
                  <Trash2 size={14} />
                </button>
              </Tooltip>
            ) : (
              <>
                <Tooltip always content={s.isFolder !== false ? t('driveView.menu.openInNewTab', { defaultValue: 'Open' }) : t('driveView.menu.open', { defaultValue: 'Open' })}>
                  <button onClick={(e) => { stop(e); openIncoming(s) }} className="ws-card-iconbtn" aria-label={t('driveView.menu.openInNewTab', { defaultValue: 'Open' })} data-testid="shared-card-open">
                    {s.isFolder !== false ? <FolderOpen size={14} /> : <ExternalLink size={14} />}
                  </button>
                </Tooltip>
                <Tooltip always content={s.isFolder !== false ? t('driveView.menu.downloadAsZip') : t('driveView.menu.download')}>
                  <button
                    onClick={(e) => {
                      stop(e)
                      downloadShare(s)
                    }}
                    className="ws-card-iconbtn"
                    aria-label={t('driveView.menu.download', { defaultValue: 'Download' })}
                    data-testid="shared-card-download"
                  >
                    <Download size={14} />
                  </button>
                </Tooltip>
                {s.canManage && (
                  <Tooltip always content={t('driveView.menu.shareLink', { defaultValue: 'Share link' })}>
                    <button onClick={(e) => { stop(e); setShareLinkTarget(s) }} className="ws-card-iconbtn" aria-label={t('driveView.menu.shareLink', { defaultValue: 'Share link' })} data-testid="shared-card-sharelink">
                      <Share2 size={14} />
                    </button>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>

        {s.permission && PERM_LABEL[s.permission] && !s.broken && (
          <span className="shrink-0 self-start">{permBadge(s.permission)}</span>
        )}
        {/* ⋮ — same glass menu the project cards use. */}
        <div className="shrink-0 self-center" onClick={stop}>
          <Tooltip always content={t('driveHome.projectsPage.moreActions', { defaultValue: 'More actions' })}>
            <button
              onClick={(e) => {
                stop(e)
                if (menu?.share.id === s.id) { setMenu(null); return }
                setMenu({ share: s, anchorEl: e.currentTarget as HTMLElement })
              }}
              className="ws-card-iconbtn"
              aria-label={t('driveHome.projectsPage.moreActions', { defaultValue: 'More actions' })}
              data-testid="shared-card-menu"
            >
              <MoreVertical size={16} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Access layer — the full-width band the mini-app cards carry, breaking
          out of the card padding via negative margins (the card's
          overflow-hidden clips it to the rounded corners). For a local manager
          it opens Who-has-access — who in THIS drive can see the mount; for a
          plain recipient it's a reserved band showing the source org, since
          they can't manage tenant access. Hidden on a revoked mount. */}
      {!s.broken && (
        s.canManage ? (
          <button
            onClick={(e) => { stop(e); setAccessTarget({ folderId: s.id, folderName: s.name, canManage: true, isFolder: s.isFolder !== false }) }}
            className="ws-project-card-footer group/foot block text-left -ml-3.5 -mb-3.5 mt-2.5 w-[calc(100%+28px)] px-3.5 py-1 transition-colors"
            aria-label={t('driveView.menu.whoHasAccess', { defaultValue: 'Who has access...' })}
            data-testid="shared-card-access"
          >
            {/* Facepile of who in THIS drive can see the mount — same line the
                mini-app cards use. Empty (no grantees/links) → a Manage-access
                hover hint, matching ProjectCard's empty state. */}
            {(s.access && ((s.access.teamCount ?? 0) + (s.access.peopleCount ?? 0) + (s.access.links ?? 0) + (s.access.outsiders ?? 0) > 0)) ? (
              <ProjectAccessLine access={s.access} memberNames={memberNames} t={t} />
            ) : (
              <span className="inline-flex items-center gap-1 min-h-[18px] text-[11px] themed-text-muted">
                {/* A restricted mount with no explicit grantees still shows its
                    lock — ProjectAccessLine only draws the lock alongside a
                    facepile, so without this an empty-but-restricted card lost
                    the indicator. */}
                {s.access?.locked && <Lock size={11} className="shrink-0" />}
                <span className="inline-flex items-center gap-1 opacity-0 group-hover/foot:opacity-100 transition-opacity">
                  <ShieldCheck size={12} />
                  {t('driveHome.projectsPage.manageAccess', { defaultValue: 'Manage access' })}
                </span>
              </span>
            )}
          </button>
        ) : (
          <div className="ws-project-card-footer opacity-70 -ml-3.5 -mb-3.5 mt-2.5 w-[calc(100%+28px)] px-3.5 py-1" onClick={stop}>
            <span className="inline-flex items-center gap-1.5 min-h-[18px] text-[11px] themed-text-muted truncate">
              {orgName
                ? t('driveHome.sharedPage.sharedByOrg', { org: orgName, defaultValue: `Shared by ${orgName}` })
                : t('driveHome.sharedPage.sharedWithDrive', { defaultValue: 'Shared with this drive' })}
            </span>
          </div>
        )
      )}
    </div>
  )

  /* ── Shared by me: a MANAGEMENT TABLE — grants are rules, not content, so
     they read as columns: folder · location · access · recipients · when. ── */

  // Recipient avatar facepile + label, shared by the manager (clickable) and
  // non-manager branches of the "Shared to" cell.
  const recipientPile = (recipients: Array<{ id: string; name: string | null }>) => (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className="flex -space-x-1 shrink-0">
        {recipients.slice(0, 4).map(r => <span key={r.id}>{orgBadge(r.name, 'w-5 h-5 text-[9px] ws-card-owner-ring')}</span>)}
        {recipients.length > 4 && (
          <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[9px] font-semibold themed-bg-primary themed-text-secondary ws-card-owner-ring">
            +{recipients.length - 4}
          </span>
        )}
      </span>
      <span className="text-xs themed-text-secondary truncate max-w-[220px]">
        {recipients.length <= 2
          ? recipients.map(r => r.name ?? '?').join(', ')
          : t('driveHome.sharedPage.driveCount', { count: recipients.length, defaultValue: `${recipients.length} drives` })}
      </span>
    </span>
  )

  const outgoingTable = (rows: typeof outgoingCards) => (
    <div className="rounded-xl overflow-hidden border themed-border" data-testid="shared-outgoing-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide themed-text-muted text-left themed-bg-primary">
            <th className="px-4 py-2.5 font-medium">{t('driveHome.sharedPage.colFolder', { defaultValue: 'Folder' })}</th>
            <th className="px-3 py-2.5 font-medium hidden md:table-cell">{t('driveHome.sharedPage.colLocation', { defaultValue: 'Location' })}</th>
            <th className="px-3 py-2.5 font-medium">{t('driveHome.sharedPage.colAccess', { defaultValue: 'Access' })}</th>
            <th className="px-3 py-2.5 font-medium">{t('driveHome.sharedPage.facetRecipient', { defaultValue: 'Shared to' })}</th>
            <th className="px-3 py-2.5 font-medium hidden sm:table-cell">{t('driveHome.sharedPage.colWhen', { defaultValue: 'Shared' })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(g => {
            const loc = folderLocation(g.path)
            // Reveal, don't OPEN — navigate to the PARENT with `?reveal=<id>`,
            // the drive's existing select-and-scroll-to affordance. A
            // `papan_`-prefixed (or null) parent means the folder sits at the
            // drive root.
            const reveal = () => {
              const atRoot = !g.parentId || g.parentId.startsWith('papan_')
              navigate(atRoot
                ? `${urlPrefix}/folder?reveal=${g.folderId}`
                : `${urlPrefix}/folder/${g.parentId}?reveal=${g.folderId}`)
            }
            return (
            <tr
              key={`${g.folderId}:${g.permission}`}
              onClick={reveal}
              className="group themed-bg-secondary hover:themed-bg-tertiary transition-colors cursor-pointer border-t themed-border-secondary"
              data-testid="shared-outgoing-row"
            >
              {/* Reveal tooltip lives on the Folder + Location cells only —
                  the rest of the row (access badge, facepile, date) isn't the
                  reveal affordance. */}
              <td className="px-4 py-2.5" title={t('driveHome.sharedPage.revealInDrive', { defaultValue: 'Reveal in drive' })}>
                <span className="inline-flex items-center gap-2 min-w-0">
                  <FileIcon name={g.folderName} isFolder={g.isFolder} size={18} />
                  <span className="themed-text font-medium truncate max-w-[220px]">{g.folderName}</span>
                </span>
              </td>
              {/* Location — where the folder lives + the reveal affordance. The
                  whole row navigates there; the chevron makes it discoverable. */}
              <td className="px-3 py-2.5 hidden md:table-cell" title={t('driveHome.sharedPage.revealInDrive', { defaultValue: 'Reveal in drive' })}>
                <span className="inline-flex items-center gap-1.5 text-xs themed-text-muted min-w-0">
                  <FolderTree size={13} className="shrink-0 opacity-70" />
                  <span className="truncate max-w-[200px]">
                    {loc.length ? loc.join(' / ') : t('driveHome.sharedPage.locationRoot', { defaultValue: 'Drive root' })}
                  </span>
                  <ArrowUpRight size={12} className="shrink-0 opacity-0 group-hover:opacity-70 transition-opacity" />
                </span>
              </td>
              <td className="px-3 py-2.5">{permBadge(g.permission)}</td>
              {/* Shared-to — the facepile opens Who-has-access (stops the row's
                  reveal navigation); non-managers get a plain, non-clickable pile. */}
              <td className="px-3 py-2.5">
                {g.canManage ? (
                  <button
                    onClick={(e) => { stop(e); setAccessTarget({ folderId: g.folderId, folderName: g.folderName, canManage: g.canManage, isFolder: g.isFolder, initialViewMode: 'outsider' }) }}
                    className="inline-flex items-center gap-1.5 min-w-0 rounded-md -mx-1 px-1 py-0.5 hover:themed-bg-primary transition-colors"
                    aria-label={t('driveHome.sharedPage.manageOutsiders', { defaultValue: 'Manage outsiders' })}
                    title={t('driveHome.sharedPage.manageOutsiders', { defaultValue: 'Manage outsiders' })}
                    data-testid="shared-outgoing-access"
                  >
                    {recipientPile(g.recipients)}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 min-w-0">{recipientPile(g.recipients)}</span>
                )}
              </td>
              <td className="px-3 py-2.5 text-xs themed-text-muted whitespace-nowrap hidden sm:table-cell">
                {g.latest ? formatRelativeDate(g.latest) : '—'}
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  const activeCount = tab === 'outgoing' ? outgoingCards.length
    : view === 'missing' ? deletedShares.length
      : visibleIncoming.length

  return (
    <div className="flex-1 overflow-y-auto" data-testid="shared-view">
      <div className="max-w-5xl mx-auto w-full px-8 py-8">
        {/* Header — title + count left, tab pills right. */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-sky-400" />
            <h1 className="text-xl font-semibold themed-text">{t('driveHome.nav.shared')}</h1>
            {!overview.isLoading && <span className="text-xs themed-text-muted mt-0.5">{activeCount}</span>}
          </div>
          {/* Toggle pills — active pill is filled, inactive stays a quiet chip. */}
          <div className="flex items-center gap-2" role="tablist">
            {(['incoming', 'outgoing'] as const).map(k => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                onClick={() => switchTab(k)}
                className={`px-3.5 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${tab === k ? 'themed-btn-primary font-medium' : 'themed-btn-secondary themed-text-muted'}`}
                data-testid={`shared-tab-${k}`}
              >
                {k === 'incoming' ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                {k === 'incoming'
                  ? t('driveHome.sharedPage.incoming', { defaultValue: 'Shared with me' })
                  : t('driveHome.sharedPage.outgoing', { defaultValue: 'Shared by me' })}
              </button>
            ))}
          </div>
        </div>

        {/* Controls: search · sort · filter · show-as-admin (ProjectsView chrome). */}
        <div className="flex items-center gap-2 mb-7 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('driveHome.sharedPage.filterPlaceholder', { defaultValue: 'Filter shares…' })}
              data-testid="shared-filter"
              className="w-full themed-input ws-drive-pill rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <button onClick={() => { setSortOpen(o => !o); setFilterOpen(false) }} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="shared-sort">
              <ArrowUpDown size={14} />{SORT_LABEL[sort]}
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-11 z-20 min-w-[170px] rounded-lg py-1 themed-bg-secondary themed-border border shadow-xl">
                {(['modified', 'shared', 'name'] as SharedSort[]).map(s => (
                  <button key={s} onClick={() => { setSort(s); setSortOpen(false) }} data-active={sort === s || undefined}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:themed-bg-tertiary transition-colors ${sort === s ? 'themed-text font-medium' : 'themed-text-muted'}`}>
                    {SORT_LABEL[s]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Facet filter */}
          <div className="relative">
            <button onClick={() => { setFilterOpen(o => !o); setSortOpen(false) }} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="shared-facet">
              <Filter size={14} />
              {facet ? facet.label : t('driveHome.projectsPage.filterBy', { defaultValue: 'Filter' })}
              {facet && <X size={13} className="opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); setFacet(null) }} />}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-11 z-20 min-w-[220px] max-h-[360px] overflow-y-auto rounded-lg py-1 themed-bg-secondary themed-border border shadow-xl">
                {facetGroups.every(g => g.options.length === 0) ? (
                  <div className="px-3 py-2 text-xs themed-text-muted">{t('driveHome.projectsPage.noFilters', { defaultValue: 'No filters available' })}</div>
                ) : facetGroups.map(g => g.options.length > 0 && (
                  <div key={g.label} className="py-1">
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wide themed-text-muted">{g.label}</div>
                    {g.options.map(f => (
                      <button key={`${f.type}:${f.value}`} onClick={() => { setFacet(f); setFilterOpen(false) }}
                        data-active={(facet?.type === f.type && facet?.value === f.value) || undefined}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:themed-bg-tertiary transition-colors truncate ${facet?.type === f.type && facet?.value === f.value ? 'themed-text font-medium' : 'themed-text'}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Show as admin — org admins/owners; server re-verifies the role. */}
          {showAdminToggle && (
            <button onClick={() => setShowAsAdmin(v => !v)} role="switch" aria-checked={showAsAdmin} data-testid="shared-show-admin"
              className="flex items-center gap-2 shrink-0 themed-text-muted">
              <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] ${showAsAdmin ? 'bg-violet-500' : 'themed-bg-primary'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all ${showAsAdmin ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
              <span className="text-xs">{t('driveHome.projectsPage.showAsAdmin', { defaultValue: 'Show as admin' })}</span>
            </button>
          )}
        </div>

        {/* Incoming lens — Active / Missing / No access. Sits under the
            controls row so it reads as "which set am I looking at", as ONE
            segmented pill (carved well + sliding chip) rather than three loose
            chips: the lenses are mutually exclusive, so a single switch says
            that where a row of buttons only implies it. */}
        {tab === 'incoming' && (
          <GlassToggle
            tablist
            className="-mt-3 mb-6 mx-auto"
            value={view}
            onChange={setView}
            testId="shared-view-pill"
            options={VIEWS.map(v => ({
              value: v.key,
              icon: <v.icon size={14} className="shrink-0" />,
              label: t(v.labelKey, { defaultValue: v.fallback }),
              testId: `shared-view-${v.key}`,
            }))}
          />
        )}

        {/* Body. The Missing lens reads the Bin, whose query is `enabled` only
            once that lens is selected — so it must gate on ITS load state too,
            or selecting Missing flashes "No deleted shares" before the rows
            arrive. */}
        {(view === 'missing' && tab === 'incoming' ? bin.isLoading : overview.isLoading) ? (
          <div className="text-sm themed-text-muted">{t('driveHome.loading')}</div>
        ) : tab === 'incoming' ? (
          (view === 'missing' ? deletedShares.length === 0 : visibleIncoming.length === 0) ? (
            <div className="text-sm themed-text-muted py-8 text-center">
              {filter.trim() || facet
                ? t('driveHome.projectsPage.noMatches', { defaultValue: 'No matches' })
                : view === 'missing'
                  ? t('driveHome.sharedPage.emptyMissing', { defaultValue: 'No deleted shares — anything you remove shows up here.' })
                  : view === 'noaccess'
                    ? t('driveHome.sharedPage.emptyNoAccess', { defaultValue: 'No revoked shares.' })
                    : t('driveHome.sharedPage.emptyIncoming', { defaultValue: "Nothing has been shared with this drive yet. Only files another drive shares with you appear here — sharing inside this drive doesn't." })}
            </div>
          ) : (
            <div data-testid="shared-incoming">
              {/* Active — grouped by the org that shared it. */}
              {view === 'active' && incomingByOrg.map(grp => (
                <section key={grp.tenantId ?? '∅'} className="mb-7" data-testid="shared-incoming-org">
                  <div className="ws-home-heading mb-2.5 flex items-center gap-2">
                    {orgBadge(grp.name, 'w-6 h-6 text-[10px]')}
                    {grp.name ?? '?'}
                    <span className="w-5 h-5 rounded-full themed-bg-secondary inline-flex items-center justify-center text-[10px] themed-text-muted font-normal">
                      {grp.items.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
                    {grp.items.map(s => incomingCard(s, grp.name))}
                  </div>
                </section>
              ))}

              {/* No access — revoked mounts, FLAT and org-less: the grant is
                  gone, so naming the source org would assert a relationship
                  that no longer exists. */}
              {view === 'noaccess' && (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3" data-testid="shared-noaccess">
                  {visibleIncoming.map(s => incomingCard(s, null))}
                </div>
              )}

              {/* Missing — deleted `.shared` rows (Bin), restorable in place. */}
              {view === 'missing' && deletedShares.length > 0 && (
                <section className="mb-7" data-testid="shared-deleted">
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
                    {deletedShares.map(i => (
                      <div
                        key={i.id}
                        className="ws-project-card text-left opacity-55"
                        style={{ '--pc-hue': projectHue(i.originalName) } as CSSProperties}
                        data-testid="shared-deleted-card"
                      >
                        <div className="flex items-center gap-2.5">
                          <span aria-hidden className="ws-project-glyph">
                            <FileIcon type={i.isFolder ? 'folder' : 'file'} isFolder={i.isFolder} size={18} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <span className="block text-sm font-medium themed-text truncate">{i.originalName}</span>
                            <span className="block text-[10px] themed-text-muted truncate mt-0.5">
                              {t('driveHome.sharedPage.deletedAgo', { when: formatRelativeDate(i.deletedAt), defaultValue: `Deleted ${formatRelativeDate(i.deletedAt)}` })}
                              {i.olderCount > 0 && ` · ${t('driveHome.sharedPage.olderInBin', { count: i.olderCount, defaultValue: `${i.olderCount} older in Bin` })}`}
                            </span>
                          </div>
                        </div>
                        <div className="ws-project-card-footer -ml-3.5 -mb-3.5 mt-2.5 w-[calc(100%+28px)] px-3.5 py-1">
                          <span className="flex items-center gap-2 min-h-[18px] text-[11px] themed-text-muted">
                            <span className="flex-1" />
                            <button
                              onClick={() => void restoreDeleted(i)}
                              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 themed-bg-primary hover:themed-text transition-colors shrink-0"
                              data-testid="shared-deleted-restore"
                            >
                              <ArrowDownLeft size={11} />
                              {t('driveHome.sharedPage.restore', { defaultValue: 'Restore' })}
                            </button>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )
        ) : (
          outgoingCards.length === 0 ? (
            <div className="text-sm themed-text-muted py-8 text-center">
              {filter.trim() || facet
                ? t('driveHome.projectsPage.noMatches', { defaultValue: 'No matches' })
                : t('driveHome.sharedPage.emptyOutgoing', { defaultValue: 'This drive hasn’t shared anything with other drives yet.' })}
            </div>
          ) : (
            <div data-testid="shared-outgoing">
              {outgoingTable(outgoingCards)}
            </div>
          )
        )}
      </div>

      {menu && (
        <FileContextMenu
          x={menuPos?.left ?? -9999}
          y={menuPos?.top ?? -9999}
          exiting={menuExiting}
          origin={menuOrigin}
          items={buildShareMenu(menu.share)}
          menuRef={menuRef}
          className={themeClass}
          testId="shared-card-context-menu"
        />
      )}

      {pointerDownload && (
        <PointerDownloadDialog
          files={[pointerDownload]}
          themeClass={themeClass}
          t={t}
          onCancel={() => setPointerDownload(null)}
          onConfirm={(choice) => {
            const row = pointerDownload
            setPointerDownload(null)
            if (choice === 'pointer') return saveShareFile(row)
            return saveShareContents(row)
          }}
        />
      )}

      {detailsTarget && <FileInfoDialog file={detailsTarget} onClose={() => setDetailsTarget(null)} />}

      {shareInternalTarget && (
        <ShareInternallyDialog
          fileId={shareInternalTarget.id}
          fileName={shareInternalTarget.name}
          tenantId={tenantId}
          isOrg={isOrg}
          isFolder={shareInternalTarget.isFolder !== false}
          myPermission={shareInternalTarget.permission ?? undefined}
          onClose={() => { setShareInternalTarget(null); void overview.refetch() }}
        />
      )}

      {shareLinkTarget && (
        <ShareLinksDialog
          fileId={shareLinkTarget.id}
          fileName={shareLinkTarget.name}
          isFolder={shareLinkTarget.isFolder !== false}
          // A mount is never an app root itself (the `.miniapp` lives inside it).
          isAppEligible={false}
          onClose={() => setShareLinkTarget(null)}
        />
      )}

      {shareOrgTarget && (
        <ShareDialog
          folderId={shareOrgTarget.id}
          folderName={shareOrgTarget.name}
          isFolder={shareOrgTarget.isFolder !== false}
          // myPermission is the SOURCE grant — a re-share can't exceed it, so
          // it's the correct ceiling. But canManage is the caller's own power
          // over the local mount (owner/admin here even at a readcopy source
          // grant); gating it on the source badge wrongly made ShareDialog
          // read-only for a local admin. Mirrors the menu/footer gate.
          myPermission={shareOrgTarget.permission ?? 'readcopy'}
          canManage={!!shareOrgTarget.canManage}
          onOpenWhoHasAccess={() => {
            setAccessTarget({ folderId: shareOrgTarget.id, folderName: shareOrgTarget.name, canManage: !!shareOrgTarget.canManage, isFolder: shareOrgTarget.isFolder !== false })
            setShareOrgTarget(null)
          }}
          onClose={() => { setShareOrgTarget(null); void overview.refetch() }}
        />
      )}

      {/* Rename modal — inside a ThemedArea, so themed-* resolves. */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRenameTarget(null)} />
          <div className="relative z-10 w-[340px] rounded-lg shadow-xl themed-bg-secondary p-4">
            <h3 className="text-sm font-semibold themed-text mb-3">{t('driveView.menu.rename', { defaultValue: 'Rename' })}</h3>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitRename(); if (e.key === 'Escape') setRenameTarget(null) }}
              onFocus={(e) => e.target.select()}
              className="w-full themed-input rounded-md px-3 py-2 text-sm mb-3"
              data-testid="shared-rename-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRenameTarget(null)} className="themed-btn-secondary rounded-md px-3 py-1.5 text-sm">{t('common.cancel', { defaultValue: 'Cancel' })}</button>
              <button onClick={() => void commitRename()} className="themed-btn-primary rounded-md px-3 py-1.5 text-sm">{t('driveView.menu.rename', { defaultValue: 'Rename' })}</button>
            </div>
          </div>
        </div>
      )}

      {accessTarget && (
        <WhoHasAccessDialog
          folderId={accessTarget.folderId}
          folderName={accessTarget.folderName}
          tenantId={tenantId}
          myPermission={accessTarget.canManage ? 'admin' : 'readcopy'}
          // Folder-only affordances (Restricted above all) key off this. An
          // outgoing share of a plain FILE showed the toggle and 404'd on
          // PATCH .../restricted, which only accepts folder-like rows.
          isFolder={accessTarget.isFolder}
          // Incoming mounts are capped by the source grant — the dialog notes it.
          isReceivedShare={/\.shared$/i.test(accessTarget.folderName)}
          inShareContext={/\.shared$/i.test(accessTarget.folderName)}
          hidePeopleTeams={!isOrg}
          // Shared-by-me recipients ARE outsider drives — open straight to that tab.
          initialViewMode={accessTarget.initialViewMode}
          onClose={() => { setAccessTarget(null); void overview.refetch() }}
        />
      )}
    </div>
  )
}
